import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, Sparkles, Briefcase, Building2 } from "lucide-react";

import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { API, setTestToken, recordEvent } from "@/lib/testApi";
import { setPathway, withPathway } from "@/lib/pathway";

// Audience-specific dropdown options. The Access form asks distinctly
// different questions for investors (who evaluate opportunities) vs
// companies (who prepare evidence for investors). Mixing these creates
// the leak the P0 report flagged.
const INVESTOR_ORG_TYPES = [
  "Single-family office",
  "Multi-family office",
  "Private investment office",
  "Wealth or asset manager",
  "Investment firm",
  "Foundation or endowment",
  "Adviser or service provider",
  "Other",
];
const COMPANY_ORG_TYPES = [
  "Early-stage startup",
  "Growth-stage company",
  "Established operating company",
  "Project sponsor / SPV",
  "Non-profit / social enterprise",
  "Public benefit corporation",
  "Adviser or service provider",
  "Other",
];
const INVESTOR_ROLES = [
  "Decision maker",
  "Committee member",
  "Analyst / due-diligence lead",
  "Adviser",
  "Other",
];
const COMPANY_ROLES = [
  "Founder / CEO",
  "Sustainability / impact lead",
  "Finance / CFO",
  "Investor relations",
  "External adviser",
  "Other",
];

const EMPTY = {
  full_name: "", professional_email: "", organization: "", job_title: "",
  role_in_decisions: "", organization_type: "", country: "", referral_code: "",
  accepted_terms: false, accepted_privacy: false, accepted_fictional_notice: false,
};

// P0 pathway routing: a client without an explicit URL pathway parameter
// must always see the neutral picker. Session storage is written when a
// pathway is chosen so downstream authenticated routes can recognise
// intent, but it never silently overrides an absent or invalid URL param.
// "Change pathway" navigates back to /access (with any stale ?path= stripped)
// so the picker renders unambiguously.
function readPathwayFromUrl(params) {
  const q = params.get("path");
  return (q === "investor" || q === "company") ? q : null;
}

export default function Access() {
  const [mode, setMode] = useState("landing");     // landing | register | signin | sent | verifying
  const [form, setForm] = useState(EMPTY);
  const [signinEmail, setSigninEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();

  // Pathway is a first-class piece of state, sourced from URL param, then
  // session storage. Setting it on this page also mirrors it to session
  // storage so the guided sample / opportunity creation carry the choice.
  const [pathway, setPathwayState] = useState(() => readPathwayFromUrl(params));
  // Keep pathway state in sync with URL parameter changes (e.g. clicking
  // "Change pathway" navigates to /access which drops ?path= — the picker
  // must re-render).
  useEffect(() => {
    const q = readPathwayFromUrl(params);
    setPathwayState(q);
  }, [params]);
  useEffect(() => { if (pathway) setPathway(pathway); }, [pathway]);

  // Handler used by the "Change pathway" affordance — navigate to /access
  // with no query string so the picker renders on a fresh URL.
  const changePathway = () => {
    setPathwayState(null);
    navigate("/access", { replace: true });
  };

  const isCompany = pathway === "company";
  const orgTypes = isCompany ? COMPANY_ORG_TYPES : INVESTOR_ORG_TYPES;
  const roles    = isCompany ? COMPANY_ROLES    : INVESTOR_ROLES;
  const roleFieldLabel = isCompany ? "Role in evidence preparation" : "Role in investment decisions";

  useEffect(() => {
    document.title = isCompany
      ? "ImpactVest Test Workspace | Build your evidence pack"
      : "ImpactVest Test Workspace | Evaluate an opportunity";
    axios.post(`${API}/test/events`, {
      event: "access_page_viewed",
      meta: { path: pathway || "unselected" },
    }).catch(() => {});
  }, [pathway, isCompany]);

  // ------- Magic-link verify handler
  useEffect(() => {
    const t = params.get("token");
    if (!t) return;
    setMode("verifying");
    (async () => {
      try {
        const res = await axios.get(`${API}/access/verify`, { params: { token: t } });
        setTestToken(res.data.token);
        toast.success("Test workspace ready.");
        navigate("/app/demo", { replace: true });
      } catch (err) {
        toast.error(err?.response?.data?.detail || "This link is no longer valid.");
        setMode("landing");
      }
    })();
  }, [params, navigate]);

  const update = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    if (!(form.accepted_terms && form.accepted_privacy && form.accepted_fictional_notice)) {
      toast.error("All three consents are required to enter the Test Workspace.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/access/register`, {
        ...form,
        referral_code: form.referral_code || null,
        path_intent: pathway || null,
      });
      recordEvent("registration_started", { organization_type: form.organization_type, path: pathway });
      setMode("sent");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to complete this request.");
    } finally {
      setBusy(false);
    }
  };

  const submitSignin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/access/request-signin`, { professional_email: signinEmail });
      setMode("sent");
    } catch {
      toast.error("Unable to complete this request.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Header />
      <main className="pt-28 md:pt-36 pb-24">
        <div className="iv-container">
          {mode === "verifying" && (
            <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
              <Loader2 className="animate-spin text-iv-gold" size={26} />
              <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-iv-fg-3">Verifying secure link…</span>
            </div>
          )}

          {mode === "sent" && (
            <div data-testid="access-magic-sent" className="iv-panel p-10 md:p-14 max-w-2xl mx-auto text-center">
              <div className="iv-eyebrow justify-center flex mb-6">Check your professional inbox</div>
              <h1 className="font-serif-display text-[38px] md:text-[52px] leading-[1.02] tracking-tight text-iv-fg">
                A secure sign-in link is on its way.
              </h1>
              <p className="mt-6 text-[15px] leading-relaxed text-iv-fg-2">
                If the details are valid, we have sent a single-use link to your professional email.
                The link expires in 15 minutes. Open it on this device to enter your ImpactVest Test Workspace.
              </p>
              <p className="mt-8 font-mono text-[11px] tracking-[0.18em] uppercase text-iv-fg-3">
                Fictional data · No confidential materials required
              </p>
            </div>
          )}

          {mode === "landing" && !pathway && (
            <div data-testid="access-pathway-picker" className="max-w-4xl mx-auto">
              <div className="iv-eyebrow mb-8">Choose your pathway</div>
              <h1 className="font-serif-display text-[38px] md:text-[54px] leading-[0.98] tracking-tight text-iv-fg">
                One evidence standard.<br />Two distinct mandates.
              </h1>
              <p className="mt-6 text-[15.5px] text-iv-fg-2 max-w-2xl leading-relaxed">
                ImpactVest serves investors evaluating opportunities and companies preparing evidence
                for diligence. Choose the pathway that reflects your role.
              </p>
              <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  data-testid="access-choose-investor"
                  aria-label="For investors — Evaluate an opportunity"
                  onClick={() => { setPathwayState("investor"); navigate("/access?path=investor", { replace: true }); }}
                  className="iv-path-card"
                >
                  <div className="flex items-center gap-2 iv-path-eyebrow">
                    <Briefcase size={13} strokeWidth={1.7} />
                    For investors
                  </div>
                  <div className="iv-path-title">Evaluate an opportunity</div>
                  <div className="iv-path-sub">Examine the evidence. Preserve the reasoning behind the decision.</div>
                  <div className="iv-path-arrow"><ArrowRight size={16} strokeWidth={1.8} /></div>
                </button>
                <button
                  data-testid="access-choose-company"
                  aria-label="For companies — Build an evidence pack"
                  onClick={() => { setPathwayState("company"); navigate("/access?path=company", { replace: true }); }}
                  className="iv-path-card"
                >
                  <div className="flex items-center gap-2 iv-path-eyebrow">
                    <Building2 size={13} strokeWidth={1.7} />
                    For companies
                  </div>
                  <div className="iv-path-title">Build an evidence pack</div>
                  <div className="iv-path-sub">Substantiate your claims. Prepare for investor diligence.</div>
                  <div className="iv-path-arrow"><ArrowRight size={16} strokeWidth={1.8} /></div>
                </button>
              </div>
              <p className="mt-8 font-mono text-[11px] tracking-[0.14em] uppercase text-iv-fg-3">
                Fictional data · No confidential materials required
              </p>
            </div>
          )}

          {mode === "landing" && pathway && (
            <>
              <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
                <div className="col-span-12 lg:col-span-7">
                  <div className="iv-eyebrow mb-8 flex items-center gap-3">
                    {isCompany ? <Building2 size={13} strokeWidth={1.7} /> : <Briefcase size={13} strokeWidth={1.7} />}
                    {isCompany ? "For companies · Test Workspace" : "For investors · Test Workspace"}
                  </div>
                  <h1 data-testid="access-headline" className="font-serif-display text-[42px] md:text-[68px] leading-[0.98] tracking-tight text-iv-fg">
                    {isCompany
                      ? <>Prove your evidence<br/>is investor-ready.</>
                      : <>Experience evidence-linked<br/>decision intelligence.</>}
                  </h1>
                  <p className="mt-7 text-[17px] leading-relaxed text-iv-fg-2 max-w-2xl">
                    {isCompany
                      ? "Explore ImpactVest through a fictional company preparing for Series A diligence, then activate a private workspace for your own evidence."
                      : "Explore ImpactVest through a fictional investment opportunity, then activate a private workspace for your Office's own materials."}
                  </p>
                  <p className="mt-4 text-[14.5px] leading-relaxed text-iv-fg-3 max-w-2xl">
                    {isCompany
                      ? "ImpactVest surfaces supported, partial and unsupported claims, contradictions and readiness gaps — never fabricates evidence, never rewrites claims silently."
                      : "ImpactVest works with the documents, investment criteria and approval process your team already uses. It preserves the evidence, judgment and human approval behind every decision."}
                  </p>
                  <div className="mt-9 flex flex-wrap items-center gap-3">
                    <button data-testid="access-cta-test" onClick={() => setMode("register")} className="iv-btn iv-btn--primary">
                      {isCompany ? "Test with fictional evidence" : "Test ImpactVest now"} <ArrowRight size={16} />
                    </button>
                    <button data-testid="access-cta-signin" onClick={() => setMode("signin")} className="iv-btn">
                      Access an existing workspace
                    </button>
                  </div>
                  <div className="mt-6 flex items-center gap-4 flex-wrap">
                    <Link data-testid="access-link-guided-sample" to={withPathway("/sample", pathway)} className="iv-link-underline text-[13.5px] text-iv-fg-2">
                      Try the guided sample first (no account needed)
                    </Link>
                    <button
                      data-testid="access-change-pathway"
                      onClick={changePathway}
                      className="font-mono text-[10.5px] tracking-[0.16em] uppercase text-iv-fg-3 hover:text-iv-fg"
                    >
                      ← Change pathway
                    </button>
                  </div>
                </div>

                <aside className="col-span-12 lg:col-span-5">
                  <div className="iv-canvas rounded-[3px] p-8">
                    <div className="iv-canvas-eyebrow mb-4">Fictional data notice</div>
                    <p className="text-[14.5px] leading-relaxed text-[var(--ivc-ivory-2)]">
                      The Test Workspace uses fictional data. No confidential documents are required,
                      and organizational approval is only needed for live use.
                    </p>
                    <ul className="mt-6 space-y-3">
                      {(isCompany
                        ? [
                          "No approval required",
                          "Enter in under a minute",
                          "Explore a fictional Series A company pack",
                          "See supported vs unsupported claims and readiness gaps",
                          "Convert to a live workspace for your own evidence",
                        ]
                        : [
                          "No approval required",
                          "Enter in under a minute",
                          "Explore a realistic investment opportunity",
                          "Complete a simulated committee approval",
                          "Convert to a live organizational workspace when ready",
                        ]
                      ).map((s) => (
                        <li key={s} className="flex items-center gap-3">
                          <Sparkles size={12} className="text-[var(--ivc-gold-2)]" />
                          <span className="text-[13.5px] leading-snug text-[var(--ivc-ivory-2)]">{s}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </aside>
              </div>
            </>
          )}

          {mode === "register" && (
            <form data-testid="access-register-form" onSubmit={submitRegister} className="mt-10 grid grid-cols-12 gap-8 md:gap-12">
              <div className="col-span-12 lg:col-span-4">
                <div className="iv-eyebrow mb-4 flex items-center gap-3">
                  {isCompany ? <Building2 size={13} strokeWidth={1.7} /> : <Briefcase size={13} strokeWidth={1.7} />}
                  {isCompany ? "For companies · Test Workspace" : "For investors · Test Workspace"}
                </div>
                <h2 className="font-serif-display text-[32px] md:text-[40px] leading-[1.02] text-iv-fg">
                  Verify your professional email.
                </h2>
                <p className="text-[14px] text-iv-fg-2 mt-4 leading-relaxed max-w-sm">
                  We use a single-use link so no password is required. Nothing is shared and no confidential
                  documents are collected.
                </p>
                <button type="button" onClick={() => setMode("landing")} className="iv-btn iv-btn--ghost mt-6">← Back</button>
              </div>
              <div className="col-span-12 lg:col-span-8 iv-panel p-6 md:p-10 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                  <Field label="Full name" required>
                    <input data-testid="ax-full-name" required value={form.full_name} onChange={update("full_name")} placeholder="Given name and family name" />
                  </Field>
                  <Field label="Professional email" required>
                    <input data-testid="ax-email" required type="email" value={form.professional_email} onChange={update("professional_email")} placeholder="name@organization.com" />
                  </Field>
                  <Field label="Organization" required>
                    <input data-testid="ax-org" required value={form.organization} onChange={update("organization")} placeholder={isCompany ? "Company or project name" : "Legal or operating name"} />
                  </Field>
                  <Field label="Job title" required>
                    <input data-testid="ax-title" required value={form.job_title} onChange={update("job_title")} placeholder={isCompany ? "e.g. Head of Sustainability" : "e.g. Head of Investments"} />
                  </Field>
                  <Field label={roleFieldLabel} required>
                    <select data-testid="ax-role" required value={form.role_in_decisions} onChange={update("role_in_decisions")}>
                      <option value="" disabled>Select…</option>
                      {roles.map((r) => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Organization type" required>
                    <select data-testid="ax-org-type" required value={form.organization_type} onChange={update("organization_type")}>
                      <option value="" disabled>Select…</option>
                      {orgTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Field>
                  <Field label="Country" required>
                    <input data-testid="ax-country" required value={form.country} onChange={update("country")} placeholder="e.g. United Kingdom" />
                  </Field>
                  <Field label="Referral code (optional)">
                    <input value={form.referral_code} onChange={update("referral_code")} placeholder="If a colleague referred you" />
                  </Field>
                </div>

                <div className="pt-2 space-y-3 border-t border-iv-line">
                  <Consent
                    label={<>I have read and accept the <Link to="/legal/terms" target="_blank" className="iv-link-underline">Terms of Use</Link>.</>}
                    checked={form.accepted_terms} onChange={update("accepted_terms")} testid="ax-c-terms" />
                  <Consent
                    label={<>I have read and accept the <Link to="/legal/privacy" target="_blank" className="iv-link-underline">Privacy Notice</Link>.</>}
                    checked={form.accepted_privacy} onChange={update("accepted_privacy")} testid="ax-c-privacy" />
                  <Consent label="I understand the Test Workspace uses fictional data and does not constitute an investment decision." checked={form.accepted_fictional_notice} onChange={update("accepted_fictional_notice")} testid="ax-c-fictional" />
                </div>

                <div className="pt-2 flex items-center gap-4">
                  <button type="submit" disabled={busy} data-testid="ax-submit" className="iv-btn iv-btn--primary">
                    {busy ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <>Send secure link <ArrowRight size={16} /></>}
                  </button>
                  <span className="font-mono text-[10.5px] tracking-[0.16em] uppercase text-iv-fg-3">
                    Single-use link · No password required
                  </span>
                </div>
              </div>
            </form>
          )}

          {mode === "signin" && (
            <form data-testid="access-signin-form" onSubmit={submitSignin} className="mt-10 grid grid-cols-12 gap-8">
              <div className="col-span-12 lg:col-span-5 lg:col-start-4 iv-panel p-8 md:p-10">
                <div className="iv-eyebrow mb-4">Returning to your Test Workspace</div>
                <h2 className="font-serif-display text-[30px] md:text-[36px] leading-tight text-iv-fg">Access an existing workspace.</h2>
                <p className="text-[14px] text-iv-fg-2 mt-3 leading-relaxed">
                  We will send a single-use link to your professional email so you can return to your workspace.
                </p>
                <label className="iv-field mt-8">
                  <span>Professional email</span>
                  <input data-testid="ax-signin-email" required type="email" value={signinEmail} onChange={(e) => setSigninEmail(e.target.value)} placeholder="name@organization.com" />
                </label>
                <div className="mt-8 flex items-center gap-4">
                  <button type="submit" disabled={busy} className="iv-btn iv-btn--primary" data-testid="ax-signin-submit">
                    {busy ? "Sending…" : "Send sign-in link"}
                  </button>
                  <button type="button" onClick={() => setMode("landing")} className="iv-btn iv-btn--ghost">← Back</button>
                </div>
              </div>
            </form>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="iv-field">
      <span>{label}{required ? <span className="text-iv-gold ml-1">·</span> : null}</span>
      {children}
    </label>
  );
}

function Consent({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input type="checkbox" className="iv-checkbox mt-0.5" checked={checked} onChange={onChange} data-testid={testid} />
      <span className="text-[13.5px] leading-relaxed text-iv-fg-2">{label}</span>
    </label>
  );
}
